import { Pipe, PipeTransform } from '@angular/core';
import { CurrencyRateService } from '@services/currency-rate.service';
import { CurrencyService } from '@services/currency.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * CurrencyConvertPipe
 *
 * Converts amounts from one currency to another using live exchange rates and formats with currency symbol.
 * This pipe performs both conversion and formatting, eliminating the need for a separate CurrencyFormatPipe.
 * Amounts are automatically rounded to the appropriate decimal points based on the target currency.
 *
 * **Impure Pipe:** This is an impure pipe that re-evaluates on every change detection cycle.
 * This ensures prices update immediately when the user changes their selected currency,
 * without requiring a page refresh. The performance impact is minimal as conversion is fast.
 *
 * Usage:
 * ```html
 * <!-- Basic: Convert from product currency to user's display currency and format -->
 * {{ product.price | currencyConvert: product.companyCurrency }}
 * Output: "USD 26.67" or "BHD 12.345"
 *
 * <!-- Convert between specific currencies -->
 * {{ 100 | currencyConvert: 'SAR' : 'USD' }}
 * Output: "USD 26.67"
 *
 * <!-- Get numeric value only (no formatting) -->
 * {{ product.price | currencyConvert: 'SAR' : 'USD' : true }}
 * Output: 26.67
 * ```
 *
 * Examples:
 * - SAR 100 → (if user currency is USD, 2 decimals) → "USD 26.67"
 * - BHD 50 → (if user currency is SAR, 2 decimals) → "SAR 498.65"
 * - JPY 1000 → (if user currency is USD, 0 decimals) → "USD 7"
 * - KWD 100 → (if user currency is BHD, 3 decimals) → "BHD 123.456"
 *
 * Decimal Points:
 * - The conversion automatically rounds to the target currency's decimal points
 * - Common examples: USD (2), JPY (0), BHD (3), KWD (3)
 * - If decimal points not available, defaults to 2
 *
 * Number Formatting:
 * - Uses locale-aware number formatting with thousand separators
 * - Format: "[CURRENCY] [AMOUNT]" (e.g., "USD 1,234.56")
 * - Respects the decimal points of the target currency
 *
 * Error Handling:
 * - If rates not loaded yet: Returns original amount with source currency
 * - If rate not found: Returns original amount with source currency
 * - If amount is invalid: Returns "0.00" or formatted zero
 *
 * @see CurrencyRateService - Manages exchange rates and conversion logic
 */
@Pipe({
    name: 'currencyConvert',
    pure: false,
    standalone: true
})
export class CurrencyConvertPipe implements PipeTransform {
    constructor(
        private currencyRateService: CurrencyRateService,
        private currencyService: CurrencyService,
        private translateService: TranslateService
    ) {}

    /**
     * Transform amount from one currency to another and format with currency symbol
     *
     * @param value - The amount to convert (numeric value)
     * @param fromCurrency - Source currency code (e.g., "SAR", "BHD", "AED") - REQUIRED
     * @param toCurrency - Target currency code (optional, defaults to user's selected display currency)
     * @param numericOnly - If true, returns numeric value only without formatting (default: false)
     * @returns Formatted currency string (e.g., "USD 26.67") or numeric value if numericOnly is true
     *
     * @example
     * // Convert SAR to user's currency and format
     * transform(100, 'SAR') // Returns: "USD 26.67" (if user currency is USD)
     *
     * @example
     * // Convert between specific currencies
     * transform(100, 'SAR', 'USD') // Returns: "USD 26.67"
     *
     * @example
     * // Get numeric value only
     * transform(100, 'SAR', 'USD', true) // Returns: 26.67
     *
     * @example
     * // Invalid fromCurrency - returns raw number without currency
     * transform(100, null) // Returns: "100.00" (no currency code)
     * transform(100, '') // Returns: "100.00" (no currency code)
     *
     * @example
     * // Invalid value inputs
     * transform(null, 'SAR') // Returns: "SAR 0.00"
     * transform(-10, 'SAR') // Returns: "SAR 0.00"
     */
    transform(
        value: number | null | undefined,
        fromCurrency: string,
        toCurrency?: string,
        numericOnly: boolean = false
    ): string | number {
        // Validate input
        if (value === null || value === undefined) {
            if (numericOnly) return 0;
            const displayCurrency =
                toCurrency ||
                this.currencyRateService.getBaseCurrency() ||
                fromCurrency;
            return this.formatCurrency(0, displayCurrency);
        }

        // Convert to number if string
        const amount = typeof value === 'string' ? parseFloat(value) : value;

        // Validate numeric value
        if (isNaN(amount) || !isFinite(amount)) {
            if (numericOnly) return 0;
            const displayCurrency =
                toCurrency ||
                this.currencyRateService.getBaseCurrency() ||
                fromCurrency;
            return this.formatCurrency(0, displayCurrency);
        }

        // Validate currency codes
        if (!fromCurrency || typeof fromCurrency !== 'string') {
            console.warn(
                '[CurrencyConvertPipe] Invalid fromCurrency - returning raw amount without currency:',
                fromCurrency
            );
            // Return raw amount with no conversion and no currency code
            // This prevents showing misleading currency symbols
            // Return amount as-is without any formatting
            return amount;
        }

        // CRITICAL FIX: Check if currency system is ready
        // If not ready (rates loading), show source currency to avoid flicker
        if (!this.currencyService.isReady) {
            // Currency system not ready - show source currency temporarily
            if (numericOnly) return amount;
            return this.formatCurrency(amount, fromCurrency.toUpperCase());
        }

        // Determine target currency
        const targetCurrency =
            toCurrency?.toUpperCase() ||
            this.currencyRateService.getBaseCurrency() ||
            fromCurrency.toUpperCase();

        // Perform conversion using service
        try {
            const convertedAmount = this.currencyRateService.convert(
                amount,
                fromCurrency.toUpperCase(),
                targetCurrency
            );

            // Check if conversion actually happened or if original amount was returned (fallback)
            // If rates aren't loaded or rate not found, service returns original amount
            const actuallyConverted =
                fromCurrency.toUpperCase() === targetCurrency ||
                this.currencyRateService.hasRate(fromCurrency.toUpperCase());

            // Return numeric value if requested
            if (numericOnly) {
                return convertedAmount;
            }

            // Format with the appropriate currency:
            // - If conversion succeeded, use target currency
            // - If conversion failed (rates not loaded), use source currency to show base price
            const displayCurrency = actuallyConverted
                ? targetCurrency
                : fromCurrency.toUpperCase();

            return this.formatCurrency(convertedAmount, displayCurrency);
        } catch (error) {
            console.error('[CurrencyConvertPipe] Conversion error:', error);

            // Fallback to original amount with source currency (base price)
            if (numericOnly) return amount;
            return this.formatCurrency(amount, fromCurrency.toUpperCase());
        }
    }

    /**
     * Format a number with currency symbol and proper decimal points
     * @param amount - The amount to format
     * @param currencyCode - Currency code (e.g., "USD", "BHD")
     * @returns Formatted string (e.g., "USD 1,234.56")
     *          For SAR, uses the new Saudi Riyal symbol (U+20C1)
     *          Currency symbol is always on the left, even in RTL mode
     */
    private formatCurrency(amount: number, currencyCode: string): string {
        // Get decimal points for this currency
        const decimalPoints =
            this.currencyRateService.getDecimalPoints(currencyCode) ?? 2;

        // Format the number with proper decimal points and thousand separators
        const formattedAmount = amount.toLocaleString('en-US', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints
        });

        let displaySymbol: string;

        // Special handling for Saudi Riyal - use the new symbol
        // U+20C1 is the official Unicode code point for Saudi Riyal symbol
        if (currencyCode.toUpperCase() === 'SAR') {
            displaySymbol = '\u20C1';
        } else {
            // Always use English currency code
            displaySymbol = currencyCode;
        }

        // Use Left-to-Right Mark (LRM) to ensure currency stays on the left in RTL
        // \u200E = Left-to-Right Mark - forces LTR rendering for the entire string
        return `\u200E${displaySymbol} ${formattedAmount}`;
    }
}
